/**
 * ResendWelcomeFlow
 * =================
 *
 * Single-screen flow for resending the welcome email to an informant
 * who hasn't yet set their own password (provider-only action).
 *
 * On mount, queries the messageHistory sub-collection for the most
 * recent welcome email timestamp.  Falls back to the informant's
 * createdAt date when no explicit log entry exists (e.g. informants
 * created before logging was introduced).
 *
 * Owns the success state internally so the parent doesn't need
 * separate resendSuccess / lastWelcomeSentAt / isLoadingLastSent flags.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatDateTime } from '../../utils/timestampUtils';
import WarningPanel from './WarningPanel';
import ConfirmationScreen from './ConfirmationScreen';
import ButtonSpinner from './ButtonSpinner';

/**
 * @param {Object}   props
 * @param {Object}   props.informant        – The informant to resend to
 * @param {string}   props.patientId        – Patient UID (for messageHistory query)
 * @param {Function} props.onResend         – (informantId) => Promise
 * @param {Function} props.onBack           – Navigate back to the detail screen
 * @param {boolean}  props.actionLoading    – Whether the backend call is in progress
 * @param {string}   props.actionError      – Error message from the backend
 * @param {Function} props.clearActionState – Reset actionError/actionSuccess
 * @param {Function} props.onFinish         – Called when user clicks "Finish" on success screen
 * @param {Object}   props.terminology      – { singular, lowerSingular }
 */
export default function ResendWelcomeFlow({
  informant,
  patientId,
  onResend,
  onBack,
  actionLoading = false,
  actionError = null,
  clearActionState,
  onFinish,
  terminology = { singular: 'Informant', lowerSingular: 'informant' },
}) {
  const [isSuccess, setIsSuccess] = useState(false);
  const [lastWelcomeSentAt, setLastWelcomeSentAt] = useState(null);
  const [isLoadingLastSent, setIsLoadingLastSent] = useState(true);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // On mount, look up the most recent welcome email timestamp from
  // messageHistory.  The backend logs entries with type = "informant_welcome"
  // and the recipient email in the `to` array.  If no record exists, fall
  // back to the informant's account creation timestamp.
  useEffect(() => {
    let cancelled = false;
    async function fetchLastSent() {
      try {
        const msgRef = collection(db, 'patients', patientId, 'messageHistory');
        const q = query(
          msgRef,
          where('type', '==', 'informant_welcome'),
          where('to', 'array-contains', informant.email),
          orderBy('sentAt', 'desc'),
          limit(1)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        if (!snap.empty) {
          setLastWelcomeSentAt(snap.docs[0].data().sentAt);
        } else if (informant.createdAt) {
          setLastWelcomeSentAt(informant.createdAt);
        }
      } catch (e) {
        console.warn('Failed to fetch last welcome email timestamp:', e);
        if (!cancelled && informant.createdAt) {
          setLastWelcomeSentAt(informant.createdAt);
        }
      } finally {
        if (!cancelled) setIsLoadingLastSent(false);
      }
    }
    fetchLastSent();
    return () => { cancelled = true; };
  }, [patientId, informant.email, informant.createdAt]);

  const handleConfirm = useCallback(async () => {
    try {
      await onResend(informant.id);
      if (mountedRef.current) setIsSuccess(true);
    } catch {
      // actionError handled by the hook
    }
  }, [onResend, informant]);

  if (isSuccess) {
    return (
      <ConfirmationScreen
        headline="Welcome email resent!"
        detail={`A new welcome email has been sent to ${informant.email}.`}
        onFinish={onFinish}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="border-b border-gray-200 pb-2">
        <h3 className="text-sm font-semibold text-gray-900">
          Resend Welcome Email
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Send a new welcome email to{' '}
          <span className="font-medium">
            {informant.firstName} {informant.lastName}
          </span>
          .
        </p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
        <dl className="grid gap-x-3 gap-y-1 text-xs" style={{ gridTemplateColumns: 'auto 1fr' }}>
          <dt className="text-gray-600">Recipient</dt>
          <dd className="font-medium text-gray-800">
            {informant.firstName} {informant.lastName}
          </dd>
          <dt className="text-gray-600">Email</dt>
          <dd className="font-medium text-gray-800 break-all">
            {informant.email}
          </dd>
        </dl>
        {isLoadingLastSent ? (
          <p className="text-xs text-gray-500 mt-2">Checking last sent…</p>
        ) : lastWelcomeSentAt ? (
          <p className="text-xs text-gray-500 mt-2">
            Welcome email last sent: {formatDateTime(lastWelcomeSentAt)}
          </p>
        ) : null}
      </div>

      <WarningPanel variant="info">
        This will send a new email with a link to set up their password and
        start answering questionnaires.
      </WarningPanel>

      {actionError && <WarningPanel variant="error">{actionError}</WarningPanel>}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { clearActionState?.(); onBack(); }}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={actionLoading}
          className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
            actionLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
          }`}
        >
          {actionLoading ? (
            <span className="inline-flex items-center gap-2">
              <ButtonSpinner />
              Sending…
            </span>
          ) : 'Resend Welcome Email'}
        </button>
      </div>
    </div>
  );
}
